import React, { useEffect, useState, useCallback, useRef } from 'react'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import dayjs from 'dayjs'
import useToastContext from 'hooks/useToastContext'
import { exportReportToExcel } from 'utils/reportExport'
import { SummaryCard, LoadingState, EmptyState } from './ScheduledVsActualReport'

// ═══ CHART.JS IMPORTS ═══
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Radar } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
)

// ═══ PAGINATION CONSTANTS ═══
const ITEMS_PER_PAGE = 10;

const AttendancePunctualityReport = ({ selectedEmployees, dateRange, onExportReady }) => {
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const notification = useToastContext()

  // ═══ PAGINATION STATE ═══
  const [currentPage, setCurrentPage] = useState(1)
  
  // ═══ REFS ═══
  const isRegistered = useRef(false);
  const isExportingRef = useRef(false);

  useEffect(() => {
    const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
    const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
    fetchData(start, end, currentPage)
  }, [selectedEmployees, dateRange, currentPage])

  // ═══ RESET PAGE TO 1 WHENEVER FILTER CHANGES ═══
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedEmployees, dateRange])

  const fetchData = async (start, end, page) => {
    setIsLoading(true)
    try {
      const employeeIDs = selectedEmployees.map(e => e.value)
      const payload = {
        startDate: start,
        endDate: end,
        page: page,
        limit: ITEMS_PER_PAGE,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      const result = await apiGet(apiPath.reportAttendancePunctuality, payload)
      
      if (result?.data?.success) {
        let finalData = []
        const rawResults = result.data.results
        
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          finalData = rawResults.results
          setTotalRecords(rawResults.total || 0)
        } else if (Array.isArray(rawResults)) {
          finalData = rawResults
          setTotalRecords(rawResults.length)
        }
        setData(finalData || [])
      } else {
        setData([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching attendance report:', error)
      notification.error('Error fetching report data')
      setData([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }

  // ═══ HANDLE EXPORT ═══
  const handleExport = useCallback(async () => {
    if (isExportingRef.current) return;
    
    isExportingRef.current = true;
    setIsExporting(true);
    
    try {
      const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
      const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
      const employeeIDs = selectedEmployees.map(e => e.value)
      
      const payload = {
        startDate: start,
        endDate: end,
        export: true,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      
      const result = await apiGet(apiPath.reportAttendancePunctuality, payload)
      
      if (result?.data?.success) {
        let allData = []
        const rawResults = result.data.results
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          allData = rawResults.results
        } else if (Array.isArray(rawResults)) {
          allData = rawResults
        }
        
        if (allData.length > 0) {
          exportReportToExcel(allData, 'Attendance & Punctuality', 'attendance_punctuality')
        } else {
          notification.error('No data available to export')
        }
      } else {
        notification.error('Failed to fetch all data for export')
      }
    } catch (error) {
      console.error('Export failed:', error)
      notification.error('Export failed')
    } finally {
      setIsExporting(false)
      isExportingRef.current = false;
    }
  }, [dateRange, selectedEmployees, notification])

  // ═══ REGISTER EXPORT FUNCTION WITH PARENT ═══
  useEffect(() => {
    if (onExportReady && !isRegistered.current && data.length > 0) {
      onExportReady(handleExport);
      isRegistered.current = true;
    }
    
    return () => {
      if (onExportReady && isRegistered.current) {
        onExportReady(null);
        isRegistered.current = false;
      }
    }
  }, [handleExport, onExportReady, data]);

  // ═══ PAGINATION CALCULATIONS ═══
  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE)

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  if (isLoading) return <LoadingState />;
  if (!data.length && !isLoading) return <EmptyState />;

  // Calculate Totals for Summary Cards
  const totalLate = data.reduce((sum, r) => sum + (r.lateDays || 0), 0)
  const totalAbsent = data.reduce((sum, r) => sum + (r.absentDays || 0), 0)
  const avgOnTimeRate = data.length > 0
    ? (data.reduce((sum, r) => sum + (r.onTimeRate || 0), 0) / data.length).toFixed(1)
    : 0

  // ═══ CHART 1: ATTENDANCE STACKED BAR (Days Worked vs Absent) ═══
  const barChartOptions = {
    indexAxis: 'y', // Horizontal
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.x} days`
        }
      }
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } }
    }
  };

  const barChartData = {
    labels: data.map(item => item.employeeName || 'Unknown'),
    datasets: [
      {
        label: 'Days Worked',
        data: data.map(item => item.daysWorked || 0),
        backgroundColor: '#60A5FA', // Soft Blue
        borderColor: '#3B82F6',
        borderWidth: 1,
        borderRadius: 3,
      },
      {
        label: 'Days Absent',
        data: data.map(item => item.absentDays || 0),
        backgroundColor: '#F87171', // Soft Red
        borderColor: '#EF4444',
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };

  // ═══ CHART 2: PUNCTUALITY RADAR CHART ═══
  // Radar charts are GREAT for showing behavioral patterns across multiple metrics
  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 10 } }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        grid: { color: '#e5e7eb' },
        pointLabels: { font: { size: 10 } },
        ticks: { 
          stepSize: 1, 
          backdropColor: 'transparent', 
          font: { size: 8 } 
        }
      }
    }
  };

  // We cap the max number of employees shown on a Radar to 6 to keep it readable
  const radarEmployees = data.slice(0, 6);

  const radarChartData = {
    labels: ['Late Arrivals', 'Early Departures', 'Missed Clock-Outs'],
    datasets: radarEmployees.map((item, index) => {
      // Generate a distinct vibrant color for each employee
      const hue = index * 50; 
      return {
        label: item.employeeName,
        data: [
          item.lateDays || 0,
          item.earlyDepartures || 0,
          item.missedClockOuts || 0
        ],
        backgroundColor: `hsla(${hue}, 80%, 60%, 0.2)`,
        borderColor: `hsl(${hue}, 80%, 50%)`,
        borderWidth: 2,
        pointBackgroundColor: `hsl(${hue}, 80%, 50%)`,
        fill: true
      };
    })
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className='grid grid-cols-3 gap-4 mb-6'>
        <SummaryCard label='Total Late Arrivals' value={totalLate} color='orange' />
        <SummaryCard label='Total Absences' value={totalAbsent} color='red' />
        <SummaryCard label='Avg On-Time Rate' value={`${avgOnTimeRate}%`} color='green' />
      </div>

      {/* ═══ CHARTS SECTION ═══ */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
        
        {/* Chart 1: Attendance Stacked Bar */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Attendance Overview</h3>
          <div className='h-64 w-full'>
            <Bar options={barChartOptions} data={barChartData} />
          </div>
        </div>

        {/* Chart 2: Punctuality Radar */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Punctuality Behavioral Map</h3>
          <div className='h-64 w-full'>
            <Radar options={radarChartOptions} data={radarChartData} />
          </div>
        </div>
      </div>

      {/* ═══ TABLE ═══ */}
      <div className='overflow-x-auto rounded-lg border border-[#E1E6EE]'>
        <table className='w-full text-xs text-left text-[#6c6c6c]'>
          <thead className='text-xs text-gray-900 bg-[#EBEFF8]'>
            <tr>
              <th className='py-3 px-4 font-medium'>Sr no.</th>
              <th className='py-3 px-4 font-medium'>Employee</th>
              <th className='py-3 px-4 font-medium'>Days Worked</th>
              <th className='py-3 px-4 font-medium'>Days Absent</th>
              <th className='py-3 px-4 font-medium'>Late Arrivals</th>
              <th className='py-3 px-4 font-medium'>Early Departures</th>
              <th className='py-3 px-4 font-medium'>Missed Clock-Outs</th>
              <th className='py-3 px-4 font-medium'>On-Time Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className='bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                <td className='py-3 px-4 font-medium text-gray-800'>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                <td className='py-3 px-4 font-medium text-gray-800'>
                  <div>{row.employeeName}</div>
                  <div className='text-gray-400 text-xs'>{row.employeeId}</div>
                </td>
                <td className='py-3 px-4 text-gray-600'>{row.daysWorked || 0}</td>
                <td className='py-3 px-4'>
                  <span className={`font-semibold ${(row.absentDays || 0) > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                    {row.absentDays || 0}
                  </span>
                </td>
                <td className='py-3 px-4'>
                  <span className={`font-semibold ${(row.lateDays || 0) > 0 ? 'text-orange-500' : 'text-gray-500'}`}>
                    {row.lateDays || 0}
                  </span>
                </td>
                <td className='py-3 px-4'>
                  <span className={`font-semibold ${(row.earlyDepartures || 0) > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
                    {row.earlyDepartures || 0}
                  </span>
                </td>
                <td className='py-3 px-4'>
                  <span className={`font-semibold ${(row.missedClockOuts || 0) > 0 ? 'text-red-400' : 'text-gray-500'}`}>
                    {row.missedClockOuts || 0}
                  </span>
                </td>
                <td className='py-3 px-4'>
                  <OnTimeBar rate={row.onTimeRate || 0} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ═══ PAGINATION FOOTER ═══ */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-4 pt-2 me-8 border-t border-gray-100'>
          <div className='text-sm text-gray-500'>
            Total <span className='font-medium text-gray-700'>{totalRecords}</span> Records
          </div>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => currentPage === 1 ? null : handlePageChange(currentPage - 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors border ${
                currentPage === 1 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Prev
            </button>
            
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-8 h-8 rounded text-sm font-medium flex items-center border justify-center transition-colors ${
                    currentPage === pageNum
                      ? 'bg-[#1E40AF] text-white'
                      : 'text-gray-600 bg-transparent hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
            
            <button
              onClick={() => currentPage === totalPages ? null : handlePageChange(currentPage + 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                currentPage === totalPages 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Sub-Component for Visual Rate Bar ──────────────────────────────────────

const OnTimeBar = ({ rate }) => {
  const color = rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-yellow-400' : 'bg-red-400'
  return (
    <div className='flex items-center gap-2'>
      <div className='w-20 bg-gray-200 rounded-full h-1.5'>
        <div className={`${color} h-1.5 rounded-full`} style={{ width: `${Math.min(rate, 100)}%` }} />
      </div>
      <span className='text-xs font-medium'>{rate.toFixed(0)}%</span>
    </div>
  )
}

export default AttendancePunctualityReport





// import React, { useEffect, useState, useCallback, useRef } from 'react'
// import { apiGet } from 'utils/apiFetch'
// import apiPath from 'utils/apiPath'
// import dayjs from 'dayjs'
// import useToastContext from 'hooks/useToastContext'
// import { exportReportToExcel } from 'utils/reportExport'
// import { SummaryCard, LoadingState, EmptyState } from './ScheduledVsActualReport'

// // ═══ PAGINATION CONSTANTS ═══
// const ITEMS_PER_PAGE = 10;

// const AttendancePunctualityReport = ({ selectedEmployees, dateRange, onExportReady }) => {
//   const [data, setData] = useState([])
//   const [totalRecords, setTotalRecords] = useState(0)
//   const [isLoading, setIsLoading] = useState(false)
//   const [isExporting, setIsExporting] = useState(false)
//   const notification = useToastContext()

//   // ═══ PAGINATION STATE ═══
//   const [currentPage, setCurrentPage] = useState(1)
  
//   // ═══ REFS ═══
//   const isRegistered = useRef(false);
//   const isExportingRef = useRef(false);

//   useEffect(() => {
//     const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
//     const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
//     fetchData(start, end, currentPage)
//   }, [selectedEmployees, dateRange, currentPage])

//   // ═══ RESET PAGE TO 1 WHENEVER FILTER CHANGES ═══
//   useEffect(() => {
//     setCurrentPage(1)
//   }, [selectedEmployees, dateRange])

//   const fetchData = async (start, end, page) => {
//     setIsLoading(true)
//     try {
//       const employeeIDs = selectedEmployees.map(e => e.value)
//       const payload = {
//         startDate: start,
//         endDate: end,
//         page: page,
//         limit: ITEMS_PER_PAGE,
//         ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
//       }
//       const result = await apiGet(apiPath.reportAttendancePunctuality, payload)
      
//       if (result?.data?.success) {
//         let finalData = []
//         const rawResults = result.data.results
        
//         if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
//           finalData = rawResults.results
//           setTotalRecords(rawResults.total || 0)
//         } else if (Array.isArray(rawResults)) {
//           finalData = rawResults
//           setTotalRecords(rawResults.length)
//         }
//         setData(finalData || [])
//       } else {
//         setData([])
//         setTotalRecords(0)
//       }
//     } catch (error) {
//       console.error('Error fetching attendance report:', error)
//       notification.error('Error fetching report data')
//       setData([])
//       setTotalRecords(0)
//     } finally {
//       setIsLoading(false)
//     }
//   }

//   // ═══ HANDLE EXPORT ═══
//   const handleExport = useCallback(async () => {
//     if (isExportingRef.current) return;
    
//     isExportingRef.current = true;
//     setIsExporting(true);
    
//     try {
//       const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
//       const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
//       const employeeIDs = selectedEmployees.map(e => e.value)
      
//       const payload = {
//         startDate: start,
//         endDate: end,
//         export: true,
//         ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
//       }
      
//       const result = await apiGet(apiPath.reportAttendancePunctuality, payload)
      
//       if (result?.data?.success) {
//         let allData = []
//         const rawResults = result.data.results
//         if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
//           allData = rawResults.results
//         } else if (Array.isArray(rawResults)) {
//           allData = rawResults
//         }
        
//         if (allData.length > 0) {
//           exportReportToExcel(allData, 'Attendance & Punctuality', 'attendance_punctuality')
//         } else {
//           notification.error('No data available to export')
//         }
//       } else {
//         notification.error('Failed to fetch all data for export')
//       }
//     } catch (error) {
//       console.error('Export failed:', error)
//       notification.error('Export failed')
//     } finally {
//       setIsExporting(false)
//       isExportingRef.current = false;
//     }
//   }, [dateRange, selectedEmployees, notification])

//   // ═══ REGISTER EXPORT FUNCTION WITH PARENT ═══
//   useEffect(() => {
//     if (onExportReady && !isRegistered.current && data.length > 0) {
//       onExportReady(handleExport);
//       isRegistered.current = true;
//     }
    
//     return () => {
//       if (onExportReady && isRegistered.current) {
//         onExportReady(null);
//         isRegistered.current = false;
//       }
//     }
//   }, [handleExport, onExportReady, data]);

//   // ═══ PAGINATION CALCULATIONS ═══
//   const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE)

//   const handlePageChange = (newPage) => {
//     if (newPage >= 1 && newPage <= totalPages) {
//       setCurrentPage(newPage)
//     }
//   }

//   if (isLoading) return <LoadingState />;
//   if (!data.length && !isLoading) return <EmptyState />;

//   // Calculate Totals for Summary Cards
//   const totalLate = data.reduce((sum, r) => sum + (r.lateDays || 0), 0)
//   const totalAbsent = data.reduce((sum, r) => sum + (r.absentDays || 0), 0)
//   const avgOnTimeRate = data.length > 0
//     ? (data.reduce((sum, r) => sum + (r.onTimeRate || 0), 0) / data.length).toFixed(1)
//     : 0

//   return (
//     <div>
//       {/* Summary Cards */}
//       <div className='grid grid-cols-3 gap-4 mb-4'>
//         <SummaryCard label='Total Late Arrivals' value={totalLate} color='orange' />
//         <SummaryCard label='Total Absences' value={totalAbsent} color='red' />
//         <SummaryCard label='Avg On-Time Rate' value={`${avgOnTimeRate}%`} color='green' />
//       </div>

//       {/* ═══ TABLE ═══ */}
//       <div className='overflow-x-auto rounded-lg border border-[#E1E6EE]'>
//         <table className='w-full text-xs text-left text-[#6c6c6c]'>
//           <thead className='text-xs text-gray-900 bg-[#EBEFF8]'>
//             <tr>
//               <th className='py-3 px-4 font-medium'>Sr no.</th>
//               <th className='py-3 px-4 font-medium'>Employee</th>
//               <th className='py-3 px-4 font-medium'>Days Worked</th>
//               <th className='py-3 px-4 font-medium'>Days Absent</th>
//               <th className='py-3 px-4 font-medium'>Late Arrivals</th>
//               <th className='py-3 px-4 font-medium'>Early Departures</th>
//               <th className='py-3 px-4 font-medium'>Missed Clock-Outs</th>
//               <th className='py-3 px-4 font-medium'>On-Time Rate</th>
//             </tr>
//           </thead>
//           <tbody>
//             {data.map((row, i) => (
//               <tr key={i} className='bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors'>
//                 <td className='py-3 px-4 font-medium text-gray-800'>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
//                 <td className='py-3 px-4 font-medium text-gray-800'>
//                   <div>{row.employeeName}</div>
//                   <div className='text-gray-400 text-xs'>{row.employeeId}</div>
//                 </td>
//                 <td className='py-3 px-4 text-gray-600'>{row.daysWorked || 0}</td>
//                 <td className='py-3 px-4'>
//                   <span className={`font-semibold ${(row.absentDays || 0) > 0 ? 'text-red-500' : 'text-gray-500'}`}>
//                     {row.absentDays || 0}
//                   </span>
//                 </td>
//                 <td className='py-3 px-4'>
//                   <span className={`font-semibold ${(row.lateDays || 0) > 0 ? 'text-orange-500' : 'text-gray-500'}`}>
//                     {row.lateDays || 0}
//                   </span>
//                 </td>
//                 <td className='py-3 px-4'>
//                   <span className={`font-semibold ${(row.earlyDepartures || 0) > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
//                     {row.earlyDepartures || 0}
//                   </span>
//                 </td>
//                 <td className='py-3 px-4'>
//                   <span className={`font-semibold ${(row.missedClockOuts || 0) > 0 ? 'text-red-400' : 'text-gray-500'}`}>
//                     {row.missedClockOuts || 0}
//                   </span>
//                 </td>
//                 <td className='py-3 px-4'>
//                   <OnTimeBar rate={row.onTimeRate || 0} />
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* ═══ PAGINATION FOOTER ═══ */}
//       {totalPages > 1 && (
//         <div className='flex items-center justify-between mt-4 pt-2 me-8 border-t border-gray-100'>
//           <div className='text-sm text-gray-500'>
//             Total <span className='font-medium text-gray-700'>{totalRecords}</span> Records
//           </div>
//           <div className='flex items-center gap-1'>
//             <button
//               onClick={() => currentPage === 1 ? null : handlePageChange(currentPage - 1)}
//               className={`px-3 py-1 rounded text-sm font-medium transition-colors border ${
//                 currentPage === 1 
//                   ? 'text-gray-300 cursor-not-allowed bg-transparent' 
//                   : 'text-gray-600 bg-transparent hover:bg-gray-100'
//               }`}
//             >
//               Prev
//             </button>
            
//             {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
//               let pageNum;
//               if (totalPages <= 5) {
//                 pageNum = i + 1;
//               } else if (currentPage <= 3) {
//                 pageNum = i + 1;
//               } else if (currentPage >= totalPages - 2) {
//                 pageNum = totalPages - 4 + i;
//               } else {
//                 pageNum = currentPage - 2 + i;
//               }
              
//               return (
//                 <button
//                   key={pageNum}
//                   onClick={() => handlePageChange(pageNum)}
//                   className={`w-8 h-8 rounded text-sm font-medium flex items-center border justify-center transition-colors ${
//                     currentPage === pageNum
//                       ? 'bg-[#1E40AF] text-white'
//                       : 'text-gray-600 bg-transparent hover:bg-gray-100'
//                   }`}
//                 >
//                   {pageNum}
//                 </button>
//               )
//             })}
            
//             <button
//               onClick={() => currentPage === totalPages ? null : handlePageChange(currentPage + 1)}
//               className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
//                 currentPage === totalPages 
//                   ? 'text-gray-300 cursor-not-allowed bg-transparent' 
//                   : 'text-gray-600 bg-transparent hover:bg-gray-100'
//               }`}
//             >
//               Next
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }

// // ─── Sub-Component for Visual Rate Bar ──────────────────────────────────────

// const OnTimeBar = ({ rate }) => {
//   const color = rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-yellow-400' : 'bg-red-400'
//   return (
//     <div className='flex items-center gap-2'>
//       <div className='w-20 bg-gray-200 rounded-full h-1.5'>
//         <div className={`${color} h-1.5 rounded-full`} style={{ width: `${Math.min(rate, 100)}%` }} />
//       </div>
//       <span className='text-xs font-medium'>{rate.toFixed(0)}%</span>
//     </div>
//   )
// }

// export default AttendancePunctualityReport